import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, TablesGrid } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { AppDispatch, RootState } from 'store'
import {
  finalizeFoodDay,
  getFoodDashboard,
  getFoodIntentsPreview,
} from 'features/food/foodSlice'
import {
  FoodMealWindowRunStatus,
  FoodPlanGenerationStatus,
  IFoodDietaryTagResponse,
  IFoodKitchenWindowSummaryResponse,
  IFoodWindowSummaryResponse,
  INextDayPreviewRow,
} from 'types/api/foodApi.interface'
import { IFoodDashboardTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { foodProvisioningModeLabel } from '../foodPlanningData'
import { formatDateForDisplay } from 'utils/helpers'
import {
  HeaderRow,
  WindowGrid,
  WindowCard,
  WindowCardTop,
  MetricTileRow,
  MetricTile,
  MetricTileValue,
  MetricTileLabel,
  AdjustNote,
  OpenWindowLink,
  WindowSelectorRow,
  WindowSelectorBtn,
  DietChipRow,
  DietChip,
  BottomGrid,
  SeverityRow,
  SeverityTile,
  SeverityValue,
  SeverityLabel,
  DayCloseWarn,
  DayCloseReady,
} from './DashboardTab.styled'

const today = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const tomorrow = () => {
  const d = today()
  d.setDate(d.getDate() + 1)
  return d
}

const formatOperationalDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const SELECTED_DAY_KEY = 'foodDashboardSelectedDay'

const readStoredDay = () => {
  const stored = sessionStorage.getItem(SELECTED_DAY_KEY)
  if (!stored) return today()
  const parsed = new Date(`${stored}T00:00:00`)
  return isNaN(parsed.getTime()) ? today() : parsed
}

const windowStatusLabel = (status: number | null) => {
  if (status === FoodMealWindowRunStatus.Open) return 'Open'
  if (status === FoodMealWindowRunStatus.Ordered) return 'Ordered'
  if (status === FoodMealWindowRunStatus.Served) return 'Served'
  if (status === FoodMealWindowRunStatus.Finalized) return 'Finalized'
  return 'No run'
}

const windowStatusPill = (status: number | null): 'success' | 'info' | 'accent' | 'neutral' => {
  if (status === FoodMealWindowRunStatus.Finalized) return 'success'
  if (status === FoodMealWindowRunStatus.Served) return 'info'
  if (status === FoodMealWindowRunStatus.Ordered) return 'accent'
  return 'neutral'
}

const planGenerationLabel = (status: number) => {
  if (status === FoodPlanGenerationStatus.Generated) return 'Generated'
  if (status === FoodPlanGenerationStatus.SkippedNoConfigVersion) return 'Skipped — no config version'
  if (status === FoodPlanGenerationStatus.SkippedNoMealWindows) return 'Skipped — no meal windows'
  if (status === FoodPlanGenerationStatus.Failed) return 'Failed'
  return 'Never ran'
}

const formatDietaryTagSet = (tags: IFoodDietaryTagResponse[]) => {
  if (!tags?.length) return 'Untagged'
  return tags.map((t) => t.code || t.name).filter(Boolean).join(' + ') || 'Untagged'
}

const windowTimes = (w: IFoodWindowSummaryResponse) => {
  const start = String(w.windowDef?.serviceStart || '').slice(0, 5)
  const end = String(w.windowDef?.serviceEnd || '').slice(0, 5)
  const cutoff = String(w.windowDef?.cutoffTime || '').slice(0, 5)
  const offset = w.windowDef?.cutoffDayOffset ? ' (prev. day)' : ''
  return { window: start && end ? `${start}–${end}` : '—', cutoff: cutoff ? `${cutoff}${offset}` : '—' }
}

const isLocked = (status: number | null) => status != null && status !== FoodMealWindowRunStatus.Open

export const DashboardTab = ({ onNavigateTab }: IFoodDashboardTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { dashboard, nextDayPreview, isLoading } = useSelector((store: RootState) => store.food)
  const [day, setDay] = useState<Date>(readStoredDay)
  const [previewDay, setPreviewDay] = useState<Date>(tomorrow())
  const [finalizing, setFinalizing] = useState(false)
  const [summaryWindowId, setSummaryWindowId] = useState<number | null>(null)

  const refreshDashboard = (d: Date) => {
    dispatch(getFoodDashboard(formatOperationalDay(d)))
  }

  useEffect(() => {
    sessionStorage.setItem(SELECTED_DAY_KEY, formatOperationalDay(day))
    refreshDashboard(day)
  }, [dispatch, day])

  useEffect(() => {
    dispatch(getFoodIntentsPreview({ day: formatOperationalDay(previewDay) }))
  }, [dispatch, previewDay])

  const windows = dashboard?.windows || []
  const openExceptions = dashboard?.openExceptions
  const dayClose = dashboard?.dayClose
  const planGeneration = dashboard?.planGeneration

  const totalOpenExceptions = useMemo(
    () =>
      openExceptions
        ? openExceptions.critical + openExceptions.high + openExceptions.medium + openExceptions.low
        : 0,
    [openExceptions],
  )

  const summaryWindow = useMemo(() => {
    if (!windows.length) return null
    const chosen = windows.find((w) => w.windowDef?.id === summaryWindowId)
    return chosen || windows.find((w) => isLocked(w.status)) || windows[0]
  }, [windows, summaryWindowId])

  const handleFinalizeDay = async () => {
    setFinalizing(true)
    const resp = await dispatch(finalizeFoodDay(formatOperationalDay(day)))
    setFinalizing(false)
    if (resp.meta.requestStatus === 'fulfilled') refreshDashboard(day)
  }

  const kitchenColumns: IHonestTableColumn<IFoodKitchenWindowSummaryResponse & { id: number }>[] = [
    { id: 'kitchen', header: 'Kitchen', cell: (row) => <BodyText weight='600'>{row.kitchen?.name}</BodyText> },
    {
      id: 'ordered',
      header: 'Ordered',
      cell: (row) =>
        summaryWindow && isLocked(summaryWindow.status) ? row.ordered : <OtherText grey>locks at cut-off</OtherText>,
    },
    { id: 'served', header: 'Served', cell: (row) => (row.confirmed ? row.served : '—') },
    { id: 'standbyUsed', header: 'Standby used', cell: (row) => (row.confirmed ? row.standbyUsed : '—') },
    {
      id: 'confirmation',
      header: 'Confirmation',
      cell: (row) => {
        if (!summaryWindow || !isLocked(summaryWindow.status)) {
          return <StatusPill status='neutral'>Opens after cut-off</StatusPill>
        }
        if (row.confirmed) return <StatusPill status='success'>Confirmed</StatusPill>
        if (row.ordered > 0) return <StatusPill status='warning'>Awaiting</StatusPill>
        return <OtherText grey>—</OtherText>
      },
    },
    {
      id: 'standby',
      header: 'Standby stock (per diet)',
      minWidth: '14rem',
      cell: (row) => (
        <DietChipRow>
          {(row.standby || []).map((s) => (
            <DietChip key={s.id} $low={s.belowThreshold}>
              {`${formatDietaryTagSet(s.dietaryTags)} ×${s.quantityOnHand}${s.belowThreshold ? ' ⚠' : ''}`}
            </DietChip>
          ))}
          {!row.standby?.length && <OtherText grey>—</OtherText>}
        </DietChipRow>
      ),
    },
  ]

  const kitchenRows = (summaryWindow?.kitchens || []).map((k, idx) => ({
    ...k,
    id: k.kitchen?.id ?? idx,
  }))

  const previewColumns: IHonestTableColumn<INextDayPreviewRow & { id: string }>[] = [
    { id: 'kitchen', header: 'Kitchen', cell: (row) => row.kitchen?.name || '—' },
    { id: 'window', header: 'Window', cell: (row) => row.windowDef?.name || '—' },
    { id: 'mode', header: 'Mode', cell: (row) => foodProvisioningModeLabel(row.mode) },
    { id: 'totalCount', header: 'Planned', cell: (row) => row.totalCount },
    {
      id: 'dietaryTagSets',
      header: 'Dietary combinations',
      minWidth: '16rem',
      cell: (row) => (
        <Flex wrap gap='0.25rem'>
          {(row.dietaryTagSets || []).map((set, idx) => (
            <StatusPill key={`${formatDietaryTagSet(set.dietaryTags)}-${idx}`} status='accent'>
              {`${formatDietaryTagSet(set.dietaryTags)} ×${set.count}`}
            </StatusPill>
          ))}
          {!row.dietaryTagSets?.length && <OtherText grey>—</OtherText>}
        </Flex>
      ),
    },
  ]

  const previewRows: (INextDayPreviewRow & { id: string })[] = (nextDayPreview || []).map((row, idx) => ({
    ...row,
    id: `${row.kitchen?.id}-${row.windowDef?.id}-${row.mode}-${idx}`,
  }))

  return (
    <Flex column gap='1rem' width='100%'>
      <HeaderRow>
        <CalendarInput
          nomarg
          labelText='Operational day'
          defaultValue={day}
          onChange={(d: Date) => d && setDay(d)}
        />
        <Button variant='primary' onClick={() => onNavigateTab?.('Meal Requests')}>
          + Create Meal Request
        </Button>
      </HeaderRow>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Meal windows</TitleText>
        </ContainerHeader>
        <ContainerContent>
          {windows.length ? (
            <WindowGrid>
              {windows.map((w) => {
                const times = windowTimes(w)
                const runId = w.mealWindowRun?.id
                return (
                  <WindowCard
                    key={w.windowDef?.id}
                    $clickable={!!runId}
                    onClick={() => runId && navigate(`/hr/food/window/${runId}`)}
                  >
                    <WindowCardTop>
                      <BodyText weight='700'>{w.windowDef?.name}</BodyText>
                      <StatusPill status={windowStatusPill(w.status)}>
                        {windowStatusLabel(w.status)}
                      </StatusPill>
                    </WindowCardTop>
                    <OtherText grey>
                      {times.window} · cut-off {times.cutoff}
                    </OtherText>
                    <MetricTileRow>
                      <MetricTile>
                        <MetricTileValue $tone='primary'>{w.planned}</MetricTileValue>
                        <MetricTileLabel>Planned</MetricTileLabel>
                      </MetricTile>
                      <MetricTile>
                        <MetricTileValue $tone='ordered'>{w.ordered}</MetricTileValue>
                        <MetricTileLabel>Ordered</MetricTileLabel>
                      </MetricTile>
                      <MetricTile>
                        <MetricTileValue $tone='served'>{w.served}</MetricTileValue>
                        <MetricTileLabel>Served</MetricTileLabel>
                      </MetricTile>
                    </MetricTileRow>
                    {w.manualAdjustmentQty > 0 && (
                      <AdjustNote>
                        + manual {w.manualAdjustmentQty}
                        {w.manualAdjustmentReasons?.length ? ` (${w.manualAdjustmentReasons[0]})` : ''}
                      </AdjustNote>
                    )}
                    {runId && <OpenWindowLink>Open window →</OpenWindowLink>}
                  </WindowCard>
                )
              })}
            </WindowGrid>
          ) : (
            <OtherText grey>No meal windows for this day.</OtherText>
          )}
        </ContainerContent>
      </Container>

      <Container>
        <ContainerHeader padding>
          <Flex between center width='100%' wrap gap='0.75rem'>
            <TitleText weight='700'>Kitchen summary</TitleText>
            <WindowSelectorRow>
              {windows.map((w) => (
                <WindowSelectorBtn
                  key={w.windowDef?.id}
                  $active={summaryWindow?.windowDef?.id === w.windowDef?.id}
                  onClick={() => setSummaryWindowId(w.windowDef?.id ?? null)}
                >
                  {w.windowDef?.name}
                </WindowSelectorBtn>
              ))}
            </WindowSelectorRow>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={kitchenColumns}
            data={kitchenRows}
            keyField='id'
            loading={isLoading}
            emptyMessage='No kitchens serve this window'
          />
        </ContainerContent>
      </Container>

      <BottomGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Exceptions</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.875rem'>
            <SeverityRow>
              <SeverityTile>
                <SeverityValue $tone='danger'>{openExceptions?.critical ?? 0}</SeverityValue>
                <SeverityLabel>Critical</SeverityLabel>
              </SeverityTile>
              <SeverityTile>
                <SeverityValue $tone='danger'>{openExceptions?.high ?? 0}</SeverityValue>
                <SeverityLabel>High</SeverityLabel>
              </SeverityTile>
              <SeverityTile>
                <SeverityValue $tone='warning'>{openExceptions?.medium ?? 0}</SeverityValue>
                <SeverityLabel>Medium</SeverityLabel>
              </SeverityTile>
              <SeverityTile>
                <SeverityValue $tone='info'>{openExceptions?.low ?? 0}</SeverityValue>
                <SeverityLabel>Low</SeverityLabel>
              </SeverityTile>
            </SeverityRow>
            <Button variant='secondary' onClick={() => onNavigateTab?.('Exceptions')}>
              Open Exceptions ({totalOpenExceptions})
            </Button>
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Operational day close</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.875rem'>
            {dayClose?.canFinalize ? (
              <DayCloseReady>All conditions met — the day can be finalized.</DayCloseReady>
            ) : (
              <DayCloseWarn>
                Finalize blocked:{' '}
                {(dayClose?.blockingReasons || []).join(' · ') || 'conditions not yet met.'}
              </DayCloseWarn>
            )}
            <Button
              variant='primary'
              disabled={!dayClose?.canFinalize || finalizing}
              onClick={handleFinalizeDay}
            >
              {finalizing ? 'Finalizing…' : 'Finalize now'}
            </Button>
          </ContainerContent>
        </Container>
      </BottomGrid>

      <TablesGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Plan generation</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Plans are generated automatically from the work schedule (debounced, roughly every 30
              seconds). There is no manual trigger.
            </OtherText>
            {planGeneration ? (
              <Flex column gap='0.5rem'>
                <StatusPill
                  status={
                    planGeneration.status === FoodPlanGenerationStatus.Generated
                      ? 'success'
                      : planGeneration.status === FoodPlanGenerationStatus.Failed
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {planGenerationLabel(planGeneration.status)}
                </StatusPill>
                <OtherText grey>
                  Last run:{' '}
                  {planGeneration.lastRunAt ? formatDateForDisplay(planGeneration.lastRunAt, true) : '—'}
                </OtherText>
                <BodyText>
                  {`${planGeneration.recordsCreated} meals created · ${planGeneration.recordsCancelled} cancelled`}
                </BodyText>
                {planGeneration.message && <OtherText grey>{planGeneration.message}</OtherText>}
              </Flex>
            ) : (
              <OtherText grey>Plan generation has never run for this day.</OtherText>
            )}
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader padding>
            <Flex between center width='100%' wrap gap='0.75rem'>
              <TitleText weight='700'>Planned intents preview</TitleText>
              <CalendarInput
                nomarg
                labelText='Day'
                defaultValue={previewDay}
                onChange={(d: Date) => d && setPreviewDay(d)}
              />
            </Flex>
          </ContainerHeader>
          <ContainerContent>
            <OtherText grey>
              Aggregated PLANNED counts only (name-free). After a window&apos;s cut-off fires, those
              meals lock to ORDERED and disappear from this preview.
            </OtherText>
            <HonestTable
              columns={previewColumns}
              data={previewRows}
              keyField='id'
              emptyMessage='No PLANNED intents for this day — the window may already be locked past cut-off'
            />
          </ContainerContent>
        </Container>
      </TablesGrid>
    </Flex>
  )
}
